import { DirectPaths } from "@/config/routes";
import type { BreadcrumbPath, BreadcrumbPathSerializable } from "./breadcrumb.types";
import {
    getPostLoginProductListingUrl,
    getProductNavKind,
} from "@/app/utils/get-nav-logo-url";

const ICM_URL_FALLBACK = "https://www.incredmoney.com";

export function getSiteOrigin(): string {
    return (process.env.ICM_URL ?? ICM_URL_FALLBACK).replace(/\/+$/, "");
}

export function destinationKey(path: { route?: string; href?: string }): string | undefined {
    return path.route ?? path.href;
}

/** Logged-out → `/`, logged-in → `{ICM_URL}/platform` (root domain, not current host). */
export function getHomeBreadcrumbRoute(isAuthenticated: boolean): string {
    if (!isAuthenticated) return DirectPaths.HOME;
    return `${getSiteOrigin()}${DirectPaths.PLATFORM}`;
}

function normalizeUrl(value: string): string {
    if (value === "/" || value === "") return "/";
    return value.replace(/\/+$/, "");
}

/** True when a crumb destination is a marketing or post-login “home”. */
export function isHomeBreadcrumbDestination(dest: string | undefined): boolean {
    if (!dest) return true;

    const normalized = normalizeUrl(dest);
    const siteOrigin = normalizeUrl(getSiteOrigin());
    const postOrigin = process.env.ICM_POST_URL
        ? normalizeUrl(process.env.ICM_POST_URL)
        : "";

    return (
        normalized === DirectPaths.HOME ||
        normalized === DirectPaths.PLATFORM ||
        normalized === siteOrigin ||
        normalized === `${siteOrigin}${DirectPaths.PLATFORM}` ||
        (postOrigin !== "" &&
            postOrigin !== "/" &&
            (normalized === postOrigin || normalized === `${postOrigin}${DirectPaths.PLATFORM}`))
    );
}

export type ResolveHomeBreadcrumbOptions = {
    /** Current pathname — used for Stocks/Global post-login Home crumb. */
    pathname?: string;
};

/**
 * Rewrites the Home crumb based on auth + page:
 * - Logged out → `/`
 * - Logged in on Stocks/Global → post-login product listing (hard-nav / refresh)
 * - Logged in elsewhere → `{ICM_URL}/platform` (root domain)
 *
 * Leaves custom `onClick`-only Home crumbs and non-home destinations alone.
 */
export function resolveHomeBreadcrumbPaths<T extends BreadcrumbPathSerializable | BreadcrumbPath>(
    paths: T[],
    isAuthenticated: boolean,
    options?: ResolveHomeBreadcrumbOptions,
): T[] {
    const homeRoute = getHomeBreadcrumbRoute(isAuthenticated);
    const pathname = options?.pathname ?? "";
    const productKind = pathname ? getProductNavKind(pathname) : null;
    const postLoginListing =
        isAuthenticated && (productKind === "stocks" || productKind === "global")
            ? getPostLoginProductListingUrl(pathname)
            : null;

    return paths.map((path) => {
        if (path.label !== "Home") return path;

        const dest = destinationKey(path);
        const hasOnClickOnly =
            typeof (path as BreadcrumbPath).onClick === "function" && !dest;
        if (hasOnClickOnly) return path;
        if (dest && !isHomeBreadcrumbDestination(dest)) return path;

        // Stocks / Global (logged in): keep a navigable dest for SEO/JSON-LD;
        // client click handler performs hard refresh → post-login listing.
        if (postLoginListing) {
            return {
                ...path,
                route: postLoginListing.startsWith("http") ? undefined : postLoginListing,
                href: postLoginListing.startsWith("http") ? postLoginListing : undefined,
            };
        }

        // Absolute ICM_URL/platform → href so cross-subdomain nav uses <a>, not Next Link.
        if (isExternalHref(homeRoute)) {
            return {
                ...path,
                route: undefined,
                href: homeRoute,
            };
        }

        return {
            ...path,
            route: homeRoute,
            href: undefined,
        };
    });
}

/** True when Home crumb should hard-refresh to the post-login product listing. */
export function shouldHardRefreshHomeBreadcrumb(
    isAuthenticated: boolean,
    pathname: string,
): boolean {
    if (!isAuthenticated) return false;
    const kind = getProductNavKind(pathname);
    return kind === "stocks" || kind === "global";
}

/**
 * Hard-navigates to the post-login Stocks/Global listing, preserving query params.
 * Same-URL assign still forces a full reload in the browser.
 */
export function hardNavigatePostLoginProductHome(pathname: string): void {
    const listingUrl = getPostLoginProductListingUrl(pathname);
    const search = typeof window !== "undefined" ? window.location.search : "";
    if (!listingUrl) {
        window.location.href = getHomeBreadcrumbRoute(true);
        return;
    }

    const withQuery = listingUrl.includes("?")
        ? listingUrl
        : `${listingUrl}${search}`;

    // Already on the listing path (same origin) → reload to preserve exact URL.
    if (!listingUrl.startsWith("http")) {
        const current = `${window.location.pathname}${window.location.search}`;
        const targetPath = `${listingUrl}${search}`;
        if (current === targetPath || window.location.pathname === listingUrl) {
            window.location.reload();
            return;
        }
        window.location.href = withQuery;
        return;
    }

    window.location.href = withQuery;
}

export function isExternalHref(dest: string): boolean {
    return dest.startsWith("http://") || dest.startsWith("https://");
}

export function toAbsoluteUrl(dest: string, siteOrigin: string): string {
    if (isExternalHref(dest)) return dest;
    const path = dest.startsWith("/") ? dest : `/${dest}`;
    return `${siteOrigin}${path}`;
}

export function buildBreadcrumbJsonLd(
    paths: BreadcrumbPathSerializable[],
    options: { siteOrigin: string; currentPageAbsoluteUrl?: string },
): object {
    const { siteOrigin, currentPageAbsoluteUrl } = options;

    const itemListElement = paths.map((path, index) => {
        const position = index + 1;
        const isLast = index === paths.length - 1;
        const dest = destinationKey(path);
        let item: string | undefined;
        if (dest) {
            item = toAbsoluteUrl(dest, siteOrigin);
        } else if (isLast && currentPageAbsoluteUrl) {
            item = currentPageAbsoluteUrl;
        }

        return {
            "@type": "ListItem",
            position,
            name: path.label,
            ...(item ? { item } : {}),
        };
    });

    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement,
    };
}
